import "@vuepic/vue-datepicker/dist/main.css";
import "./assets/css/fontawesome.min.css";
import "./assets/css/solid.min.css";
import "./assets/css/light.min.css";
import "./assets/css/thin.min.css";
import "./assets/css/duotone.min.css";
import "mapbox-gl/dist/mapbox-gl.css";
import { createPinia } from "pinia";
import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import { LDPlugin } from "launchdarkly-vue-client-sdk";
import router from "./router";
import App from "./App.vue";
import tooltipPlugin from "./plugins/tooltip";
import { piniaPlugin } from "./plugins/piniaRouter";
import datePlugin from "./plugins/date";
import timeAgoPlugin from "./plugins/timeAgo";
import statusPlugin from "./plugins/status";
import { plugin as formKitPlugin, defaultConfig } from "@formkit/vue";
import "./style/app.css";
import formKitConfig from "../formkit.config";
import EditEdiFormField from "./components/waybilling/EditEdiFormField.vue";
import { VueQueryPlugin, queryClient } from "./plugins/vueQuery";
import { SSO_CONFIG_KEY } from "./utils/auth";
import { userSSOApi } from "./api/adapters/external-auth";
import { createAuth0App } from "./lib/auth0";
import { createOktaApp } from "./lib/okta";
import { useAuthSession } from "./composables/useAuthSession";

const app = createApp(App);
const pinia = createPinia();

pinia.use(piniaPlugin(router));

if (import.meta.env.PROD) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    release: import.meta.env.VITE_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.vueIntegration({ tracingOptions: { trackComponents: true, hooks: ["mount"] } }),
    ],
    tracePropagationTargets: ["dashboard.telegraph.io", "preview.telegraph.io", /^\//],
    // Set sample rates to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.7,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

function registerPlugins() {
  app.component("EditEdiFormField", EditEdiFormField);
  app.use(pinia);
  app.use(router);
  app.use(tooltipPlugin);
  app.use(datePlugin);
  app.use(timeAgoPlugin);
  app.use(statusPlugin);
  app.use(formKitPlugin, defaultConfig(formKitConfig));
  app.use(LDPlugin, { deferInitialization: true });
  app.use(VueQueryPlugin, { queryClient });
}

app.config.errorHandler = (error, instance, info) => {
  console.error("Uncaught error from " + (instance?.$options?.name || "child component") + ":", { error, info });
  if (import.meta.env.PROD) {
    Sentry.captureException(error, { extra: { instance, info } });
  }
};

async function bootstrapApplication() {
  const ssoResponse = await userSSOApi.getConfig();
  if (!ssoResponse?.config) {
    registerPlugins();
    return app.mount("#app");
  }

  // Provide SSO config globally to application
  app.provide(SSO_CONFIG_KEY, ssoResponse?.config);
  const provider = ssoResponse?.config.auth.provider;
  const { authProvider, logoutUrl } = useAuthSession();

  authProvider.value = provider ?? "telegraph";
  logoutUrl.value = ssoResponse.config.logout_url;

  if (provider === "auth0") {
    // Setup the Auth0 client
    createAuth0App(app, ssoResponse.config);
  }

  if (provider === "okta") {
    createOktaApp(app, ssoResponse.config);
  }

  registerPlugins();
  app.mount("#app");
}

bootstrapApplication();
